import type { TimeRange } from '@mimir/common/util/timeRange';
import type Timeline from '@mimir/client-common/timeline/Timeline';
import type { ItemAccessControl } from '@mimir/common/dto/itemAccessControlDto';
import type { ClientItem } from '@mimir/common/dto/searchResultHit';
import type { ItemCreationRequestDto } from '@mimir/common/dto/itemCreationRequestDto';
import type { SubclipParams } from '@mimir/common/dto/subclipDto';
import type { StartAnalyticsJobRequestDto } from '@mimir/common/dto/itemModels';
import type { DynamoItemType} from '@mimir/common/dto/itemTypes';
import { ItemType } from '@mimir/common/dto/itemTypes';
import type { ItemMetaData } from '@mimir/common/dto/fieldValue';
import type { AnalyticConfig } from '@mimir/common/dto/tenantModel';
import type { CreateVirtualClipPayload } from '@mimir/common/dto/virtualClipUtil';
import type { VirtualClipModel } from '@mimir/common/dto/virtualClipModel';
import { isInstanceOfVirtualClip } from '@mimir/common/dto/virtualClipUtil';
import { normalizeAnalyticConfigForItem } from '../model/upload-util';
import { createVirtualClip, startBackendJob, updateClipList, updateTimeline } from './api';
import type { ClipListUpdateDto } from '@mimir/common/dto/clipListDto';
import type { ItemMediaPlayerOps } from '../model/itemMediaPlayerOps';
import type { AnyTimelineFormat, TimelineDto } from '@mimir/common/dto/cutter/timelineDto';
import type { SearchState } from '../model/searchState';
import { folders } from '../globals/folders';


/**
 * User's shortcut to create a clean copy: holds `alt + ctrl (or command)` + click-on-create-item.
 */
export function shouldCreateCleanCopy(mouseEvent: MouseEvent | undefined): boolean {
  return mouseEvent !== undefined && ((mouseEvent.altKey && mouseEvent.ctrlKey) || mouseEvent.metaKey);
}


export const doCreateItem = async (
  searchState: SearchState,
  folderId: string | undefined,
  metadata: ItemMetaData,
  analyticConfig: AnalyticConfig,
  visibleTo: ItemAccessControl | undefined,
  updatableBy: ItemAccessControl | undefined,
  doCreateCleanCopy: boolean,
  type: DynamoItemType | undefined,
  shouldInject: boolean,
  itemIdToCopyMetadataFrom: string | undefined,
) => {

  const placeholder: Partial<ItemCreationRequestDto> = {
    metadata: metadata,
    itemType: type ?? ItemType.FILE,
    folderParents: folderId ? [folderId] : undefined,
    itemIdToCopyMetadataFrom,
    visibleTo,
    updatableBy,
    executeSameActionOnArchiveCopy: doCreateCleanCopy,
    // A newly created graphics will behave like a blank one, so
    // there is no need for using a placeholder item state
    initialItemState: type === ItemType.GRAPHICS ? 'complete' : undefined,
    ...normalizeAnalyticConfigForItem(analyticConfig),
  };

  const hit = await searchState.ops.createPlaceHolderAndSearchHit({
    item: placeholder,
    inject: shouldInject,
    currentFolder: folders.currentFolder,
  });
  return hit;
};

export async function createNewVirtualClip(
  timeline: Timeline,
  playerOps: ItemMediaPlayerOps,
  item: Pick<ClientItem, 'id' | 'virtualClipInfo'>,
  folderId: string | undefined,
  metadata: ItemMetaData,
  visibleTo: ItemAccessControl | undefined,
  updatableBy: ItemAccessControl | undefined
) {
  const masterClipId = isInstanceOfVirtualClip(item.virtualClipInfo)
    ? item.virtualClipInfo.masterClipId
    : item.id;

  const { startOffset, inPoint, outPoint } = getVirtualClipMarkers(timeline, playerOps, item);
  const virtualClipInfo: VirtualClipModel | CreateVirtualClipPayload = {
    metadata,
    masterClipId,
    parentClipId: item.id === masterClipId ? undefined : item.id,
    startOffsetMasterClip: startOffset,
    inPoint,
    outPoint,
    folderParents: folderId ? [folderId] : undefined,
    visibleTo,
    updatableBy,
  };
  await createVirtualClip(virtualClipInfo);
}

export function getVirtualClipMarkers(
  timeline: Timeline,
  playerOps: ItemMediaPlayerOps,
  item: Pick<ClientItem, 'virtualClipInfo'>
) {
  const startOffset = item.virtualClipInfo?.inPoint ?? 0;
  const virtualClipStart = playerOps.getVirtualClipStartObs().currentValue();
  const virtualClipDurationInMs = playerOps.getVirtualClipDurationObs().currentValue().approximate * 1000.0;
  return {
    startOffset,
    // TODO: This does not make sense? First we inject virtualClipStart to
    // getMarkIn function in order to get the mark in relative to the
    // virtualClipStart and then we add this offset. Is there any point in
    // this?
    inPoint: timeline.getMarkIn(virtualClipStart) + startOffset,
    outPoint: timeline.getMarkOut(virtualClipDurationInMs, virtualClipStart) + startOffset
  };
}

export const getSubClipMarkers = (item: Pick<ClientItem, 'virtualClipInfo'>, marks: TimeRange) => {
  const startTimeOffset = isInstanceOfVirtualClip(item.virtualClipInfo) ? item.virtualClipInfo.inPoint : 0;
  return {
    startTime: marks.start + startTimeOffset,
    endTime: marks.end + startTimeOffset,
  };
};

export const createSubClip = async (currentItem: ClientItem, subclipParams: SubclipParams)  => {
  const payload: StartAnalyticsJobRequestDto = {
    itemId : isInstanceOfVirtualClip(currentItem.virtualClipInfo) ? currentItem.virtualClipInfo.masterClipId : currentItem.id,
    operationType: 'subclip',
    operationParams: subclipParams,
  };

  return await startBackendJob(payload);
};

export const doCreateClipListItem = async (
  searchState: SearchState,
  folderId: string | undefined,
  metadata: ItemMetaData,
  analyticConfig: AnalyticConfig,
  visibleTo: ItemAccessControl | undefined,
  updatableBy: ItemAccessControl | undefined,
  clipList: ClipListUpdateDto,
) => {
  const item = await doCreateItem(searchState, folderId, metadata, analyticConfig, visibleTo, updatableBy, false, ItemType.CLIP_LIST, true, undefined);
  await updateClipList(item.id, clipList, 0);
  return item.id;
};

export const doCreateTimelineItem = async (
  searchState: SearchState,
  folderId: string | undefined,
  metadata: ItemMetaData,
  analyticConfig: AnalyticConfig,
  visibleTo: ItemAccessControl | undefined,
  updatableBy: ItemAccessControl | undefined,
  timeline: AnyTimelineFormat,
) => {
  const item = await doCreateItem(searchState, folderId, metadata, analyticConfig, visibleTo, updatableBy, false, ItemType.TIMELINE, true, undefined);
  const timelineDto: TimelineDto = {
    timeline,
    timelineVersion: 0,
  };
  await updateTimeline(item.id, timelineDto);
  return item.id;
};

export const doCreateGraphicsItem = async (
  searchState: SearchState,
  folderId: string | undefined,
  metadata: ItemMetaData,
  analyticConfig: AnalyticConfig,
  visibleTo: ItemAccessControl | undefined,
  updatableBy: ItemAccessControl | undefined,
) => {
  const item = await doCreateItem(searchState, folderId, metadata, analyticConfig, visibleTo, updatableBy, false, ItemType.GRAPHICS, true, undefined);

  // TODO: store a blank graphics template

  return item.id;
};
